import React, { useState, useEffect, useContext, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../../sharedModules/dataTable/DataTable/model';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

const formatDate = (value: string | null | undefined) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const formatAmount = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(num)) return '-';
  return `$${num.toFixed(2)}`;
};

const VendorReminderListPage: React.FunctionComponent = () => {
  const history = useHistory();
  const routerLocation = useLocation();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const queryParams = new URLSearchParams(routerLocation.search);
  const initialLocation = queryParams.get('location') || 'all';

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [locationFilter, setLocationFilter] = useState<string>(initialLocation);
  const [detailsRow, setDetailsRow] = useState<any | null>(null);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const fetchControllerRef = useRef<AbortController | null>(null);

  const updateUrl = (loc: string) => {
    const params = new URLSearchParams();
    if (loc && loc !== 'all') params.set('location', loc);
    const qs = params.toString();
    history.replace(`/vendors/reminders${qs ? `?${qs}` : ''}`);
  };

  const fetchReminders = async (loc = 'all') => {
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    fetchControllerRef.current = controller;

    setLoading(true);
    try {
      const params: any = {};
      if (loc && loc !== 'all') params.location = loc;
      const response: any = await axios.get('/vendors/vendor_reminders', { params, signal: controller.signal });
      setRows(response.data || []);
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch vendor reminders';
      showErrorSnackBar(errorMessage);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const handleLocationChange = (newLocation: string) => {
    setLocationFilter(newLocation);
    updateUrl(newLocation);
    fetchReminders(newLocation);
  };

  const handleSendReminder = async (row: any) => {
    setSendingId(row.id);
    try {
      await axios.post(`/vendors/vendor_reminders/${row.id}/send_reminder`);
      showSuccessSnackBar('Reminder sent');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to send reminder';
      showErrorSnackBar(errorMessage);
    } finally {
      setSendingId(null);
    }
  };

  useEffect(() => {
    fetchReminders(initialLocation);
  }, []);

  const columns: IDataTableColumn<any>[] = [
    {
      name: 'Statement Received At',
      selector: 'statement_received_at',
      sortable: true,
      format: (row: any) => formatDate(row.statement_received_at),
    },
    {
      name: 'Contact Name',
      selector: 'resolved_vendor_name',
      sortable: true,
      wrap: true,
      format: (row: any) => row.resolved_vendor_name || '-',
    },
    {
      name: 'Vendor ID',
      selector: 'vendor_id',
      sortable: true,
      format: (row: any) => row.vendor_id || '-',
    },
    {
      name: 'Reminder Email',
      selector: 'reminder_email',
      sortable: true,
      wrap: true,
      format: (row: any) => row.reminder_email || '-',
    },
    {
      name: 'Location',
      selector: 'location',
      sortable: true,
      format: (row: any) => row.location || '-',
    },
    {
      name: 'Missing Invoices',
      selector: 'missing_invoices_count',
      sortable: true,
      format: (row: any) => {
        const count = row.missing_invoices_count || 0;
        return <Chip label={`${count} missing invoice${count === 1 ? '' : 's'}`} color="error" size="small" />;
      },
    },
    {
      name: 'Actions',
      selector: 'actions',
      sortable: false,
      format: (row: any) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={sendingId === row.id}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleSendReminder(row);
          }}
        >
          {sendingId === row.id ? 'Sending…' : 'Send Reminder'}
        </Button>
      ),
    },
  ];

  return (
    <Box style={{ width: '100%' }}>
      <Grid justifyContent="space-between" container spacing={3}>
        <Grid item>
          <Typography variant="h5" component="h5" style={{ marginTop: 5 }}>
            Vendor Reminders
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
            Statements with line items that are missing from both ServMan and DocuWare.
          </Typography>
        </Grid>
      </Grid>
      <br />
      <Grid container spacing={2} alignItems="flex-start" style={{ marginBottom: 16 }}>
        <Grid item xs={12} md={3}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', marginBottom: 0.5 }}>
            Location
          </Typography>
          <ToggleButtonGroup
            value={locationFilter}
            exclusive
            onChange={(_e, val) => val && handleLocationChange(val)}
            size="small"
            color="primary"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="pittsburgh">Pittsburgh</ToggleButton>
            <ToggleButton value="tampa">Tampa</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>
      <Card>
        <CardContent>
          <DataTable
            title="Reminders"
            columns={columns}
            data={rows}
            onRowClicked={(row) => setDetailsRow(row)}
            striped
            dense
            highlightOnHover
            pointerOnHover
            noHeader
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            progressPending={loading}
          />
        </CardContent>
      </Card>

      <Dialog open={!!detailsRow} onClose={() => setDetailsRow(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Missing Invoices — {detailsRow?.resolved_vendor_name || 'Unknown'}
        </DialogTitle>
        <DialogContent dividers>
          {detailsRow && (
            <Box>
              <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Vendor ID</Typography>
                  <Typography variant="body2">{detailsRow.vendor_id || '-'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Location</Typography>
                  <Typography variant="body2">{detailsRow.location || '-'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Statement Received At</Typography>
                  <Typography variant="body2">{formatDate(detailsRow.statement_received_at)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Reminder Email</Typography>
                  <Typography variant="body2">{detailsRow.reminder_email || '-'}</Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                {(detailsRow.missing_line_items || []).length} missing invoice
                {(detailsRow.missing_line_items || []).length === 1 ? '' : 's'}
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detailsRow.missing_line_items || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="textSecondary">
                          No missing line items.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {(detailsRow.missing_line_items || []).map((li: any) => (
                    <TableRow key={li.id}>
                      <TableCell>{li.invoice_number || '-'}</TableCell>
                      <TableCell>{li.description || '-'}</TableCell>
                      <TableCell align="right">{li.quantity ?? '-'}</TableCell>
                      <TableCell align="right">{formatAmount(li.unit_price)}</TableCell>
                      <TableCell align="right">{formatAmount(li.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {detailsRow && (
            <Button
              variant="contained"
              color="primary"
              disabled={sendingId === detailsRow.id}
              onClick={() => handleSendReminder(detailsRow)}
            >
              {sendingId === detailsRow.id ? 'Sending…' : 'Send Reminder'}
            </Button>
          )}
          <Button onClick={() => setDetailsRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorReminderListPage;
